<template>
  <ContextColorProvider context="trade">
    <div :class="computedNavBarClass">
      <NavBar>
        <template #left>
          <BackButton />
        </template>
        <template #center>
          <p>Meus ativos</p>
        </template>
        <template #right>
          <ButtonIconContextual
            v-if="currentTabGroup === PositionsGroups.STOCK_LENDING"
            icon="EF0051"
            label="Dúvidas frequentes"
            class="positions__button-faq"
            @click="handleFrequentlyAskedQuestions"
          />

          <ShowMoneyToggleButton />
        </template>
      </NavBar>
      <template v-if="!error">
        <TabsListLoader v-if="loading" class="positions__tabs" />
        <Tabs
          v-else-if="hasTabs"
          class="positions__tabs"
          :on-change="onChangeTab"
          :selected-index="currentTabIndex"
        >
          <TabList data-testid="tab-list">
            <Tab v-for="tab in tabsData" :key="tab.description.value">{{
              tab.description.formatted
            }}</Tab>
          </TabList>
        </Tabs>
      </template>
    </div>
    <main class="positions__main">
      <TabContentLoader v-if="loading" />
      <HelperViewStatus
        v-else-if="error"
        class="positions__helper-view"
        title="Não foi possível carregar os seus ativos"
        description="Aguarde um momento e tente novamente"
        button-label="Tentar novamente"
        status="alert"
        @click="fetchPositionsTabs"
      />
      <HelperViewStatus
        v-else-if="!hasTabs"
        class="positions__helper-view"
        title="Nenhum ativo foi encontrado"
        description="Você não tem posição em nenhum ativo"
        status="information"
      />
      <template v-else>
        <CashMarket v-if="currentTabGroup === PositionsGroups.CASH_MARKET" />
        <Derivatives v-if="currentTabGroup === PositionsGroups.DERIVATIVES" />
        <StockLending
          v-if="currentTabGroup === PositionsGroups.STOCK_LENDING"
          :has-positions.sync="hasStockLendingPositions"
          :paid-custody-active.sync="paidCustodyActive"
        />
        <CustomOperations
          v-if="currentTabGroup === PositionsGroups.CUSTOM_OPERATIONS"
        />
      </template>
    </main>

    <ModalPaidCustodyCanceled
      v-if="hasStockLendingPositions && !paidCustodyActive"
      :show-modal="modalPaidCustodyCanceled"
      @close="handleCloseModalPaidCustodyCanceled"
    />

    <footer v-if="showButtonCancelPaidCustody" class="positions__footer">
      <ButtonContainer>
        <template #button-primary>
          <ButtonSecondary
            label="Desativar custódia remunerada"
            @click="handleNextClick"
          />
        </template>
      </ButtonContainer>
    </footer>
  </ContextColorProvider>
</template>

<script lang="ts">
import { Component, Vue } from 'vue-property-decorator';

import {
  ContextColorProvider,
  NavBar,
  Tabs,
  TabList,
  Tab,
  SkeletonLoader,
  HelperViewStatus,
  ButtonIconContextual,
  ButtonContainer,
  ButtonSecondary
} from '@warrenbrasil/nebraska-web';
import {
  ShowMoneyToggleButton,
  BackButton
} from '@/modules/common/components/buttons';
import { CashMarket } from './components/CashMarket';
import { StockLending } from './components/StockLending';
import { Derivatives } from './components/Derivatives';
import { CustomOperations } from './components/CustomOperations';
import { TabsListLoader, TabContentLoader } from './components/Loaders';

import { NebraskaColors } from '@warrenbrasil/nebraska-tokens-web';
import {
  getPositionsSummary,
  PositionsGroups,
  PositionSummary
} from '@/modules/trade/services/instruments';
import { ModalPaidCustodyCanceled } from './components/ModalPaidCustodyCanceled';

@Component({
  components: {
    ContextColorProvider,
    BackButton,
    NavBar,
    ShowMoneyToggleButton,
    CashMarket,
    StockLending,
    Tabs,
    TabList,
    Derivatives,
    Tab,
    SkeletonLoader,
    TabsListLoader,
    TabContentLoader,
    HelperViewStatus,
    CustomOperations,
    ButtonIconContextual,
    ButtonContainer,
    ButtonSecondary,
    ModalPaidCustodyCanceled
  }
})
export default class TradePositionsView extends Vue {
  readonly NebraskaColors = NebraskaColors;
  readonly PositionsGroups = PositionsGroups;

  tabsData: PositionSummary[] = [];
  currentTabIndex = 0;
  loading = false;
  error = false;
  tabSelected = this.$route?.query?.group as PositionsGroups;
  paidCustodyActive = false;
  hasStockLendingPositions = false;
  modalPaidCustodyCanceled = true;

  async created() {
    await this.fetchPositionsTabs();
  }

  get computedNavBarClass() {
    return ['positions__navbar', 'positions__navbar--border'];
  }

  get currentTabGroup() {
    return this.tabsData[this.currentTabIndex]?.description.value;
  }

  get hasTabs() {
    return this.tabsData.length > 0;
  }

  get showButtonCancelPaidCustody() {
    return (
      this.currentTabGroup === PositionsGroups.STOCK_LENDING &&
      this.paidCustodyActive
    );
  }

  updateHasStockLendingPositions(value: boolean) {
    this.hasStockLendingPositions = value;
  }

  handleCloseModalPaidCustodyCanceled() {
    this.modalPaidCustodyCanceled = false;
  }

  handleNextClick() {
    return this.$router.push({
      name: 'paidCustody',
      params: { status: 'cancel' }
    });
  }

  setGroupQuery(group: PositionsGroups) {
    const query = { ...this.$route.query, group };
    this.$router.replace({ query });
  }

  onChangeTab(selectedTabIndex: number) {
    this.currentTabIndex = selectedTabIndex;
    const tabGroup = this.tabsData[selectedTabIndex].description.value;

    if (this.$route.query?.group !== tabGroup) this.setGroupQuery(tabGroup);
  }

  setInitialTab() {
    const group = this.$route.query?.group as PositionsGroups;
    const tabIndex = this.tabsData.findIndex(
      tab => tab.description.value === group
    );

    this.onChangeTab(tabIndex === -1 ? 0 : tabIndex);
  }

  async fetchPositionsTabs() {
    try {
      this.loading = true;
      this.error = false;

      this.tabsData = await getPositionsSummary();

      if (this.tabsData.length > 0) this.setInitialTab();
    } catch {
      this.error = true;
    } finally {
      this.loading = false;
    }
  }

  handleFrequentlyAskedQuestions() {
    return this.$router.push({ name: 'faq' });
  }
}
</script>

<style lang="less" scoped>
.positions {
  &__button-faq {
    margin-right: @size-spacing-x400;
  }

  &__navbar {
    position: sticky;
    z-index: 20;
    top: 0;
    left: 0;
    width: 100%;

    &--border {
      border-bottom: @size-border-x400 solid @divider-primary;
    }
  }

  &__tabs {
    max-width: 1600px;
    padding: 0 @size-spacing-x500;
    margin: 0 auto;
    background-color: @background-secondary;

    .to-lg({ padding: 0 @size-spacing-x400; });
  }

  &__main {
    max-width: 1600px;
    margin: 0 auto;
    padding-bottom: @size-spacing-x1200;
  }

  &__helper-view {
    margin: @size-spacing-x1800 auto @size-spacing-x400 auto;
  }

  &__footer {
    position: fixed;
    bottom: 0;
    width: 100%;
    background-color: @background-secondary;
    z-index: 1;
  }
}
</style>
